/**
 * Side-by-side campaign comparison bar chart using Nivo Bar.
 */

import { ResponsiveBar } from '@nivo/bar'
import { useNivoTheme, useChartColors } from './nivoTheme'

export interface CampaignComparisonData {
  campaign: string
  [metric: string]: string | number
}

interface CampaignComparisonProps {
  data: CampaignComparisonData[]
  metrics?: string[]
  height?: number
  highlights?: string[]
  layout?: 'vertical' | 'horizontal'
  valueFormat?: 'number' | 'currency' | 'percent'
  onBarClick?: (campaign: string, metric: string, value: number) => void
}

const METRIC_LABELS: Record<string, string> = {
  spend: 'Spend',
  impressions: 'Impressions',
  clicks: 'Clicks',
  conversations: 'Conversations',
  leads: 'Leads',
  purchases: 'Purchases',
  ctr: 'CTR',
  cpc: 'CPC',
  roas: 'ROAS',
  costPerLead: 'CPL',
}

function formatValue(value: number, format: 'number' | 'currency' | 'percent'): string {
  if (format === 'currency') {
    return `$${(value / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  if (format === 'percent') {
    return `${value.toFixed(2)}%`
  }
  return value.toLocaleString()
}

export function CampaignComparison({
  data,
  metrics = ['spend'],
  height = 300,
  highlights: _highlights,
  layout = 'vertical',
  valueFormat = 'number',
  onBarClick,
}: CampaignComparisonProps) {
  const theme = useNivoTheme()
  const chartColors = useChartColors()

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        No comparison data available
      </div>
    )
  }

  const keys = metrics.map((m) => METRIC_LABELS[m] || m)

  // Transform data to use metric labels as keys
  const barData = data.map((item) => {
    const result: Record<string, string | number> = { campaign: item.campaign }
    metrics.forEach((m) => {
      result[METRIC_LABELS[m] || m] = item[m] ?? 0
    })
    return result
  })

  return (
    <div style={{ height }}>
      <ResponsiveBar
        data={barData}
        keys={keys}
        indexBy="campaign"
        theme={theme}
        margin={{
          top: 20,
          right: 20,
          bottom: layout === 'vertical' ? 60 : 20,
          left: layout === 'horizontal' ? 120 : 60,
        }}
        layout={layout}
        padding={0.3}
        groupMode={metrics.length > 1 ? 'grouped' : 'stacked'}
        colors={(_d) => {
          const idx = keys.indexOf(String(_d.id))
          return chartColors[idx >= 0 ? idx : 0]
        }}
        borderRadius={4}
        enableLabel={data.length <= 6}
        labelSkipWidth={40}
        labelSkipHeight={16}
        labelTextColor={{ from: 'color', modifiers: [['brighter', 3]] }}
        axisBottom={layout === 'vertical' ? {
          tickRotation: data.length > 4 ? -30 : 0,
          tickSize: 5,
          tickPadding: 5,
        } : undefined}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          format: (v: number) => formatValue(v, valueFormat),
        }}
        motionConfig="gentle"
        onClick={(datum) => {
          if (!onBarClick) return
          onBarClick(String(datum.indexValue), String(datum.id), Number(datum.value))
        }}
        tooltip={({ id, value, indexValue, color }) => (
          <div className="bg-popover rounded-lg shadow-lg px-3 py-2 text-sm border border-border">
            <p className="font-semibold text-popover-foreground">{String(indexValue)}</p>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-muted-foreground">{String(id)}:</span>
              <span className="font-medium">{formatValue(Number(value), valueFormat)}</span>
            </div>
          </div>
        )}
        legends={metrics.length > 1 ? [{
          dataFrom: 'keys' as const,
          anchor: 'top-right' as const,
          direction: 'row' as const,
          translateX: 0,
          translateY: -20,
          itemsSpacing: 16,
          itemWidth: 80,
          itemHeight: 16,
          symbolSize: 10,
        }] : []}
      />
    </div>
  )
}
